/**
 * Socket.IO Client Service - Gestione delle notifiche in tempo reale
 */
import { io } from 'socket.io-client';

let socket = null;
let notificationCallbacks = [];

/**
 * Inizializza la connessione Socket.IO
 * @param {Object} userData - Dati utente (accountId, userId, userName)
 */
export const initializeSocket = (userData) => {
    if (socket?.connected) {
        return socket;
    }
    
    // Ottieni l'URL base del server
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
    
    // Estrai solo host:port (rimuovi path come /api)
    const urlObj = new URL(baseUrl);
    const wsUrl = `${urlObj.protocol}//${urlObj.host}`;
    
    socket = io(wsUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
    });
    
    socket.on('connect', () => {
        
        // Autentica l'utente
        if (userData) {
            socket.emit('authenticate', {
                accountId: userData.accountId,
                userId: userData.userId || userData._id,
                userName: userData.name || userData.email
            });
        }
    });
    
    socket.on('authenticated', (data) => {
    });
    
    socket.on('notification', (notification) => {
        // Notifica tutti i callback registrati
        notificationCallbacks.forEach(callback => {
            try {
                callback(notification);
            } catch (err) {
                console.error('Errore nel callback notifica:', err);
            }
        });
    });
    
    socket.on('disconnect', (reason) => {
    });
    
    socket.on('connect_error', (error) => {
        console.error('❌ Errore connessione Socket.IO:', error.message);
    });
    
    return socket;
};

/**
 * Disconnetti Socket.IO
 */
export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
};

/**
 * Registra un callback per le notifiche
 * @param {Function} callback - Funzione da chiamare quando arriva una notifica
 * @returns {Function} Funzione per rimuovere il callback
 */
export const onNotification = (callback) => {
    notificationCallbacks.push(callback);
    
    // Ritorna funzione per rimuovere il callback
    return () => {
        notificationCallbacks = notificationCallbacks.filter(cb => cb !== callback);
    };
};

/**
 * Marca una notifica come letta
 * @param {string} notificationId - ID della notifica
 */
export const markNotificationRead = (notificationId) => {
    if (socket?.connected) {
        socket.emit('markNotificationRead', { notificationId });
    }
};

/**
 * Marca tutte le notifiche come lette
 */
export const markAllNotificationsRead = () => {
    if (socket?.connected) {
        socket.emit('markAllNotificationsRead');
    }
};

/**
 * Verifica se il socket è connesso
 * @returns {boolean}
 */
export const isConnected = () => {
    return socket?.connected || false;
};

/**
 * Ottieni l'istanza del socket
 * @returns {Socket}
 */
export const getSocket = () => socket;

export default {
    initializeSocket,
    disconnectSocket,
    onNotification,
    markNotificationRead,
    markAllNotificationsRead,
    isConnected,
    getSocket
};
